import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Typography, Tag, message, Row, Col, Statistic,
  Modal, Popconfirm, Tooltip, Tabs, Form, Input, Select, Divider, Alert,
  Badge, Drawer, Descriptions, InputNumber, Checkbox
} from 'antd';
import {
  PlusOutlined, UserAddOutlined, TeamOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, FileTextOutlined, SendOutlined, MailOutlined, CheckCircleOutlined,
  CloseCircleOutlined, DownloadOutlined, SearchOutlined, BankOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';
import { JobPosting } from '../../types/recruitment';
import JobPostingForm from '../../components/Recruitment/JobPostingForm';
import JobPostingDetails from '../../components/Recruitment/JobPostingDetails';
import PDFExportModal from '../../components/Recruitment/PDFExportModal';
import ApplicationsKanban from '../../components/Recruitment/ApplicationsKanban';

const { Title } = Typography;
const { TextArea } = Input;

const statusColors: Record<string,string> = {
  draft:'default', active:'success', paused:'warning', closed:'error',
  submitted:'processing', screening:'warning', interview:'blue',
  assessment:'purple', offer:'cyan', hired:'success', rejected:'error',
};

const RecruitmentHiring: React.FC = () => {
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [offerLetters, setOfferLetters] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [bgChecks, setBgChecks] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Job modals
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobPosting | null>(null);

  // Application modals
  const [appModal, setAppModal] = useState(false);
  const [appForm] = Form.useForm();

  // Offer letter modals
  const [offerModal, setOfferModal] = useState(false);
  const [offerForm] = Form.useForm();
  const [sendOfferModal, setSendOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [sendingOffer, setSendingOffer] = useState(false);

  // Template modals
  const [templateModal, setTemplateModal] = useState(false);
  const [editTemplateModal, setEditTemplateModal] = useState(false);
  const [sendTemplateModal, setSendTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateForm] = Form.useForm();
  const [editTemplateForm] = Form.useForm();
  const [sendTemplateForm] = Form.useForm();
  const [previewModal, setPreviewModal] = useState(false);

  // Background check modals
  const [bgModal, setBgModal] = useState(false);
  const [bgForm] = Form.useForm();
  const [bgReportModal, setBgReportModal] = useState(false);
  const [bgReportForm] = Form.useForm();
  const [selectedBgChecks, setSelectedBgChecks] = useState<number[]>([]);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => { fetchData(); fetchExtras(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jr, ar, emr] = await Promise.all([
        apiService.getJobPostings(),
        apiService.getJobApplications(),
        apiService.getEmployees(),
      ]);
      if (jr.success) setJobs(jr.data || jr.jobs || []);
      if (ar.success) setApplications(ar.data || ar.applications || []);
      if (emr.success) setEmployees(emr.data || []);
    } finally { setLoading(false); }
  };

  const fetchExtras = async () => {
    const [or, tr, br] = await Promise.all([
      apiService.getOfferLetters(),
      apiService.getCommTemplates(),
      apiService.getBackgroundChecks(),
    ]);
    if (or.success) setOfferLetters(or.data || []);
    if (tr.success) setTemplates(tr.data || []);
    if (br.success) setBgChecks(br.data || []);
  };

  // ── Job actions ────────────────────────────────────────────────────────────
  const handleDeleteJob = async (id: string) => {
    const r = await apiService.deleteJobPosting(id);
    if (r.success) { message.success('Job posting deleted'); fetchData(); }
    else message.error(r.error || 'Failed');
  };

  // ── Application actions ────────────────────────────────────────────────────
  const handleCreateApp = async (values: any) => {
    const r = await apiService.createApplication(values);
    if (r.success) { message.success(`Application ${r.application_id} created`); setAppModal(false); appForm.resetFields(); fetchData(); }
    else message.error(r.error || 'Failed');
  };

  const handleMoveApp = async (id: number, status: string) => {
    const r = await apiService.updateApplicationStatus(id, status);
    if (r.success) { message.success(`Moved to ${status}`); fetchData(); }
    else message.error(r.error || 'Failed');
  };

  // ── Offer letter actions ───────────────────────────────────────────────────
  const handleCreateOffer = async (values: any) => {
    const r = await apiService.createOfferLetter({ ...values, offered_salary: Number(values.offered_salary), tenant_id: apiService.getTenantId() });
    if (r.success) {
      message.success('Offer letter created');
      if (r.email_sent) message.info(`Email sent to candidate`);
      setOfferModal(false); offerForm.resetFields(); fetchExtras();
    } else message.error(r.error || 'Failed');
  };

  const handleSendOffer = async () => {
    if (!selectedOffer) return;
    setSendingOffer(true);
    const r = await apiService.sendOfferLetterEmail(selectedOffer.id);
    setSendingOffer(false);
    if (r.success) { message.success('Offer letter emailed to candidate'); setSendOfferModal(false); fetchExtras(); }
    else message.error(r.error || r.email_result?.error || 'Failed to send email');
  };

  const handleOfferResponse = async (id: number, status: 'accepted' | 'rejected') => {
    const r = await apiService.updateOfferLetter({ id, status, response_date: new Date().toISOString().split('T')[0], tenant_id: apiService.getTenantId() });
    if (r.success) { message.success(`Offer ${status}`); fetchExtras(); }
    else message.error(r.error || 'Failed');
  };

  // ── Template actions ────────────────────────────────────────────────────────
  const handleCreateTemplate = async (values: any) => {
    const r = await apiService.createCommTemplate(values);
    if (r.success) { message.success('Template created'); setTemplateModal(false); templateForm.resetFields(); fetchExtras(); }
    else message.error(r.error || 'Failed');
  };

  const handleEditTemplate = async (values: any) => {
    const r = await apiService.updateOfferLetter({ ...values, id: selectedTemplate?.id, _type: 'template' });
    // Use the correct endpoint
    const r2 = await apiClient_put_template(selectedTemplate?.id, values);
    if (r2.success) { message.success('Template updated'); setEditTemplateModal(false); fetchExtras(); }
    else message.error(r2.error || 'Failed');
  };

  const apiClient_put_template = async (id: number, values: any) => {
    try {
      const axios = (await import('axios')).default;
      const base = (window as any).__nexus_api_base || '/backend/hr-api';
      const r = await axios.put(
        `${base}/recruitment/recruitment_extras.php?type=template&tenant_id=${apiService.getTenantId()}`,
        { ...values, id, tenant_id: apiService.getTenantId() }
      );
      return r.data;
    } catch { return { success: false }; }
  };

  const handleSendTemplate = async (values: any) => {
    if (!selectedTemplate) return;
    const recipients = values.recipients.split(',').map((e: string) => ({ email: e.trim(), name: '' }));
    const variables: Record<string,string> = {};
    (values.variable_pairs || []).forEach((p: any) => { if (p.key && p.value) variables[p.key] = p.value; });
    const r = await apiService.sendTemplate(selectedTemplate.id, recipients, variables);
    if (r.success) { message.success(r.message); setSendTemplateModal(false); sendTemplateForm.resetFields(); }
    else message.error(r.message || r.error || 'Failed');
  };

  const handleDeleteTemplate = async (id: number) => {
    const r = await apiService.deleteCommTemplate(id);
    if (r.success) { message.success('Template deleted'); fetchExtras(); }
    else message.error(r.error || 'Failed');
  };

  // ── Background check actions ────────────────────────────────────────────────
  const handleCreateBg = async (values: any) => {
    const r = await apiService.createBackgroundCheck({ ...values, status: 'in_progress' });
    if (r.success) { message.success(`Background check ${r.check_id} initiated`); setBgModal(false); bgForm.resetFields(); fetchExtras(); }
    else message.error(r.error || 'Failed');
  };

  const handleBgUpdate = async (id: number, status: string) => {
    const r = await apiService.updateBackgroundCheck({ id, status, completed_date: new Date().toISOString().split('T')[0] });
    if (r.success) { message.success(`Check marked ${status}`); fetchExtras(); }
    else message.error(r.error || 'Failed');
  };

  const handleEmailReport = async (values: any) => {
    setSendingReport(true);
    const r = await apiService.emailBackgroundCheckReport(
      values.recipients, values.subject, values.message || '', selectedBgChecks
    );
    setSendingReport(false);
    if (r.success) { message.success(r.message); setBgReportModal(false); bgReportForm.resetFields(); setSelectedBgChecks([]); }
    else message.error(r.message || r.error || 'Failed');
  };

  // ── Job columns ────────────────────────────────────────────────────────────
  const jobColumns = [
    { title: 'Job Title', dataIndex: 'title', render: (v: string, r: any) => (
      <div><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department} • {r.location}</div></div>
    )},
    { title: 'Type', dataIndex: 'employment_type', render: (v: string) => <Tag>{v?.replace('_',' ')}</Tag> },
    { title: 'Salary Range', key: 'salary', render: (_: any, r: any) => r.salary_min ? `₦${Number(r.salary_min).toLocaleString()} – ₦${Number(r.salary_max).toLocaleString()}` : '—' },
    { title: 'Deadline', dataIndex: 'application_deadline', render: (v: string) => v || '—' },
    { title: 'Positions', dataIndex: 'positions_available' },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={statusColors[v] || 'default'}>{v?.toUpperCase()}</Tag> },
    { title: 'Actions', render: (_: any, r: any) => (
      <Space>
        <Tooltip title="View"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedJob(r); setIsDetailsModalVisible(true); }} /></Tooltip>
        <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditingJob(r); setIsFormModalVisible(true); }} /></Tooltip>
        <Tooltip title="Export PDF"><Button type="text" size="small" icon={<FileTextOutlined />} onClick={() => { setSelectedJob(r); setIsPdfModalVisible(true); }} /></Tooltip>
        <Popconfirm title="Delete this job posting?" onConfirm={() => handleDeleteJob(String(r.id))}>
          <Button type="text" size="small" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      </Space>
    )},
  ];

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'jobs',
      label: '💼 Job Postings',
      children: (
        <Card extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={() => { setSelectedJob(null); setIsPdfModalVisible(true); }}>Export PDF</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingJob(null); setIsFormModalVisible(true); }}>Post New Job</Button>
          </Space>
        }>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}><Card size="small"><Statistic title="Total Postings" value={jobs.length} /></Card></Col>
            <Col span={6}><Card size="small"><Statistic title="Active" value={jobs.filter(j => j.status==='active').length} valueStyle={{ color: '#52c41a' }} /></Card></Col>
            <Col span={6}><Card size="small"><Statistic title="Applications" value={applications.length} prefix={<TeamOutlined />} /></Card></Col>
            <Col span={6}><Card size="small"><Statistic title="Hired" value={applications.filter(a => a.status==='hired').length} valueStyle={{ color: '#1890ff' }} /></Card></Col>
          </Row>
          <Table dataSource={jobs} rowKey="id" loading={loading} columns={jobColumns} pagination={{ pageSize: 10 }} />
        </Card>
      ),
    },
    {
      key: 'pipeline',
      label: '📋 Applications Pipeline',
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ color: '#666', fontSize: 13 }}>Drag cards between stages to update candidate status. Use the button to manually add a candidate.</span>
            </div>
            <Button type="primary" icon={<UserAddOutlined />} onClick={() => setAppModal(true)}>Add Candidate</Button>
          </div>
          <ApplicationsKanban key="kanban" />
          <Divider>Applications List View</Divider>
          <Table dataSource={applications} rowKey="id" pagination={{ pageSize: 10 }} size="small"
            columns={[
              { title: 'Candidate', dataIndex: 'applicant_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.applicant_email}</div></> },
              { title: 'Job', dataIndex: 'job_title' },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Experience', dataIndex: 'experience_years', render: (v: number) => `${v} yrs` },
              { title: 'Expected Salary', dataIndex: 'expected_salary', render: (v: number) => v ? `₦${Number(v).toLocaleString()}` : '—' },
              { title: 'Notice Period', dataIndex: 'notice_period', render: (v: string) => v || '—' },
              { title: 'Stage', dataIndex: 'status', render: (v: string) => <Tag color={statusColors[v] || 'default'}>{v?.toUpperCase()}</Tag> },
              { title: 'Applied', dataIndex: 'applied_at', render: (v: string) => v?.split('T')[0] },
              { title: 'Actions', render: (_: any, r: any) => (
                <Select size="small" value={r.status} style={{ width: 130 }}
                  onChange={v => handleMoveApp(r.id, v)}>
                  {['submitted','screening','interview','assessment','offer','hired','rejected'].map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
                </Select>
              )},
            ]} />
        </div>
      ),
    },
    {
      key: 'offers',
      label: '📄 Offer Letters',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOfferModal(true)}>Create Offer Letter</Button>}>
          <Alert message="Best Practice: Only send offer letters after verbal offer acceptance and reference checks are complete." type="info" showIcon style={{ marginBottom: 16 }} />
          <Table dataSource={offerLetters} rowKey="id" pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Offer ID', dataIndex: 'offer_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
              { title: 'Candidate', dataIndex: 'candidate_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.candidate_email}</div></> },
              { title: 'Position', dataIndex: 'position_title' },
              { title: 'Department', dataIndex: 'department' },
              { title: 'Offered Salary', dataIndex: 'offered_salary', render: (v: number) => v ? `₦${Number(v).toLocaleString()}` : '—' },
              { title: 'Start Date', dataIndex: 'start_date', render: (v: string) => v || '—' },
              { title: 'Expires', dataIndex: 'offer_expiry', render: (v: string) => v || '—' },
              { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v==='accepted'?'success':v==='rejected'?'error':v==='sent'?'processing':v==='expired'?'warning':'default'}>{v?.toUpperCase()}</Tag> },
              { title: 'Actions', render: (_: any, r: any) => (
                <Space>
                  {r.status === 'draft' && (
                    <Button size="small" icon={<SendOutlined />} type="primary"
                      onClick={() => { setSelectedOffer(r); setSendOfferModal(true); }}>
                      Send
                    </Button>
                  )}
                  {r.status === 'sent' && (
                    <Space>
                      <Button size="small" icon={<CheckCircleOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }}
                        onClick={() => handleOfferResponse(r.id, 'accepted')}>Accept</Button>
                      <Button size="small" icon={<CloseCircleOutlined />} danger
                        onClick={() => handleOfferResponse(r.id, 'rejected')}>Reject</Button>
                      <Button size="small" icon={<SendOutlined />}
                        onClick={() => { setSelectedOffer(r); setSendOfferModal(true); }}>Resend</Button>
                    </Space>
                  )}
                </Space>
              )},
            ]} />
        </Card>
      ),
    },
    {
      key: 'templates',
      label: '📧 Comm Templates',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setTemplateModal(true)}>Create Template</Button>}>
          <Alert message="Templates are used to communicate with candidates at each pipeline stage. Use {{variable}} syntax for dynamic fields. Templates can also be triggered automatically when a candidate's stage changes." type="info" showIcon style={{ marginBottom: 16 }} />
          <Table dataSource={templates} rowKey="id" pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Template', dataIndex: 'template_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.subject}</div></> },
              { title: 'Type', dataIndex: 'template_type', render: (v: string) => <Tag color="blue">{v?.replace(/_/g,' ')}</Tag> },
              { title: 'Variables', dataIndex: 'variables', render: (v: string) => v?.split(',').slice(0,3).map((vr: string, i: number) => <Tag key={i} color="cyan" style={{ fontSize: 10 }}>{vr.trim()}</Tag>) },
              { title: 'Status', dataIndex: 'is_active', render: (v: number) => <Tag color={v ? 'success' : 'default'}>{v ? 'Active' : 'Inactive'}</Tag> },
              { title: 'Actions', render: (_: any, r: any) => (
                <Space>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedTemplate(r); setPreviewModal(true); }}>Preview</Button>
                  <Button size="small" icon={<EditOutlined />} onClick={() => { setSelectedTemplate(r); editTemplateForm.setFieldsValue(r); setEditTemplateModal(true); }}>Edit</Button>
                  <Button size="small" icon={<SendOutlined />} type="primary" onClick={() => { setSelectedTemplate(r); setSendTemplateModal(true); }}>Send</Button>
                  <Popconfirm title="Delete this template?" onConfirm={() => handleDeleteTemplate(r.id)}>
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </Space>
              )},
            ]} />
        </Card>
      ),
    },
    {
      key: 'background',
      label: '🔍 Background Checks',
      children: (
        <Card extra={
          <Space>
            <Button icon={<MailOutlined />} onClick={() => { setSelectedBgChecks(bgChecks.map(b => b.id)); setBgReportModal(true); }}>Email Report</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setBgModal(true)}>Initiate Check</Button>
          </Space>
        }>
          <Alert message="Background checks require written candidate consent. Results are confidential and must be handled in accordance with Nigerian data protection law (NDPR)." type="warning" showIcon style={{ marginBottom: 16 }} />
          <Table dataSource={bgChecks} rowKey="id" pagination={{ pageSize: 10 }}
            rowSelection={{ selectedRowKeys: selectedBgChecks, onChange: (keys) => setSelectedBgChecks(keys as number[]) }}
            columns={[
              { title: 'Check ID', dataIndex: 'check_id', render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code> },
              { title: 'Candidate', dataIndex: 'candidate_name', render: (v: string) => <div style={{ fontWeight: 600 }}>{v}</div> },
              { title: 'Check Type', dataIndex: 'check_type', render: (v: string) => <Tag color="purple">{v?.replace(/_/g,' ')}</Tag> },
              { title: 'Provider', dataIndex: 'provider', render: (v: string) => v || '—' },
              { title: 'Initiated', dataIndex: 'initiated_date' },
              { title: 'Completed', dataIndex: 'completed_date', render: (v: string) => v || '—' },
              { title: 'Result Notes', dataIndex: 'result_notes', render: (v: string) => v || '—', ellipsis: true },
              { title: 'Status', dataIndex: 'status', render: (v: string) => <Badge status={v==='passed'?'success':v==='failed'?'error':v==='in_progress'?'processing':'default'} text={v?.replace(/_/g,' ')} /> },
              { title: 'Actions', render: (_: any, r: any) => r.status === 'in_progress' ? (
                <Space>
                  <Button size="small" style={{ color: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleBgUpdate(r.id, 'passed')}>Pass</Button>
                  <Button size="small" danger onClick={() => handleBgUpdate(r.id, 'failed')}>Fail</Button>
                  <Button size="small" onClick={() => handleBgUpdate(r.id, 'inconclusive')}>Inconclusive</Button>
                </Space>
              ) : (
                <Button size="small" icon={<MailOutlined />} onClick={() => { setSelectedBgChecks([r.id]); setBgReportModal(true); }}>Email Report</Button>
              )},
            ]} />
          {selectedBgChecks.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button icon={<MailOutlined />} onClick={() => setBgReportModal(true)}>
                Email Report for {selectedBgChecks.length} selected check(s)
              </Button>
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Recruitment & Hiring</Title>
      <Tabs items={tabItems} defaultActiveKey="jobs" />

      {/* ── Add Candidate Modal ──────────────────────────────────────────── */}
      <Modal title="Add Candidate to Pipeline" open={appModal}
        onCancel={() => { setAppModal(false); appForm.resetFields(); }} footer={null} width={650}>
        <Alert message="Use this form to manually add referrals, walk-in candidates, or applications received via email." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={appForm} layout="vertical" onFinish={handleCreateApp}>
          <Form.Item name="job_id" label="Job Posting" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select job">
              {jobs.filter(j => j.status === 'active').map(j => (
                <Select.Option key={j.id} value={j.id}>{j.title} — {j.department}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="applicant_name" label="Candidate Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="applicant_email" label="Email" rules={[{ required: true }, { type: 'email' }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="applicant_phone" label="Phone"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="notice_period" label="Notice Period"><Input placeholder="e.g. 1 month, immediate" /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="experience_years" label="Years Experience"><InputNumber min={0} max={50} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="current_salary" label="Current Salary (₦)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="expected_salary" label="Expected Salary (₦)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item name="status" label="Initial Stage" initialValue="submitted">
            <Select>{['submitted','screening','interview','assessment'].map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="cover_letter" label="Notes / Cover Letter Summary"><TextArea rows={3} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Add Candidate</Button><Button onClick={() => setAppModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* ── Create Offer Letter Modal ─────────────────────────────────────── */}
      <Modal title="Create Offer Letter" open={offerModal}
        onCancel={() => { setOfferModal(false); offerForm.resetFields(); }} footer={null} width={680}>
        <Form form={offerForm} layout="vertical" onFinish={handleCreateOffer}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="candidate_name" label="Candidate Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="candidate_email" label="Candidate Email" rules={[{ required: true }, { type: 'email' }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="position_title" label="Position" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="department" label="Department"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="offered_salary" label="Gross Annual Salary (₦)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} /></Form.Item></Col>
            <Col span={8}><Form.Item name="start_date" label="Proposed Start Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="offer_expiry" label="Offer Expiry Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="status" label="Create as" initialValue="draft">
            <Select>
              <Select.Option value="draft">Draft (save only)</Select.Option>
              <Select.Option value="sent">Send immediately via email</Select.Option>
            </Select>
          </Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Offer Letter</Button><Button onClick={() => setOfferModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* ── Send Offer Letter Confirmation ────────────────────────────────── */}
      <Modal title="Send Offer Letter" open={sendOfferModal}
        onCancel={() => setSendOfferModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setSendOfferModal(false)}>Cancel</Button>,
          <Button key="send" type="primary" icon={<SendOutlined />} loading={sendingOffer} onClick={handleSendOffer}>Send via Email</Button>,
        ]} width={500}>
        {selectedOffer && (
          <div>
            <p>You are about to send an offer letter to:</p>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Candidate">{selectedOffer.candidate_name}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedOffer.candidate_email}</Descriptions.Item>
              <Descriptions.Item label="Position">{selectedOffer.position_title}</Descriptions.Item>
              <Descriptions.Item label="Offered Salary">₦{Number(selectedOffer.offered_salary).toLocaleString()}</Descriptions.Item>
            </Descriptions>
            <Alert message="Once sent, the offer status will change to 'Sent' and the candidate can accept or reject." type="warning" showIcon />
          </div>
        )}
      </Modal>

      {/* ── Create Template Modal ─────────────────────────────────────────── */}
      <Modal title="Create Communication Template" open={templateModal}
        onCancel={() => { setTemplateModal(false); templateForm.resetFields(); }} footer={null} width={700}>
        <Alert message="Use {{variable_name}} syntax for dynamic content e.g. {{candidate_name}}, {{position}}, {{company_name}}, {{interview_date}}" type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={templateForm} layout="vertical" onFinish={handleCreateTemplate}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="template_name" label="Template Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="template_type" label="Stage / Type" rules={[{ required: true }]}>
              <Select>{['application_received','shortlisted','interview_invite','rejected','offer_letter','onboarding_welcome','general'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Form.Item name="subject" label="Email Subject" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="body" label="Email Body" rules={[{ required: true }]}><TextArea rows={10} placeholder="Dear {{candidate_name}},&#10;&#10;..." /></Form.Item>
          <Form.Item name="variables" label="Variables (comma-separated)"><Input placeholder="{{candidate_name}},{{position}},{{company_name}}" /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Template</Button><Button onClick={() => setTemplateModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* ── Edit Template Modal ──────────────────────────────────────────── */}
      <Modal title={`Edit Template — ${selectedTemplate?.template_name}`} open={editTemplateModal}
        onCancel={() => { setEditTemplateModal(false); editTemplateForm.resetFields(); }} footer={null} width={700}>
        <Form form={editTemplateForm} layout="vertical" onFinish={async (values) => {
          const r = await apiClient_put_template(selectedTemplate?.id, values);
          if (r.success) { message.success('Template updated'); setEditTemplateModal(false); editTemplateForm.resetFields(); fetchExtras(); }
          else message.error(r.error || 'Failed');
        }}>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="template_name" label="Template Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="template_type" label="Stage / Type">
              <Select>{['application_received','shortlisted','interview_invite','rejected','offer_letter','onboarding_welcome','general'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
          </Row>
          <Form.Item name="subject" label="Email Subject" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="body" label="Email Body" rules={[{ required: true }]}><TextArea rows={10} /></Form.Item>
          <Form.Item name="variables" label="Variables"><Input /></Form.Item>
          <Form.Item name="is_active" label="Status" initialValue={1}>
            <Select><Select.Option value={1}>Active</Select.Option><Select.Option value={0}>Inactive</Select.Option></Select>
          </Form.Item>
          <Space><Button type="primary" htmlType="submit">Save Changes</Button><Button onClick={() => setEditTemplateModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* ── Send Template Modal ──────────────────────────────────────────── */}
      <Modal title={`Send Template — ${selectedTemplate?.template_name}`} open={sendTemplateModal}
        onCancel={() => { setSendTemplateModal(false); sendTemplateForm.resetFields(); }} footer={null} width={600}>
        {selectedTemplate && (
          <div>
            <Alert message={`Subject: ${selectedTemplate.subject}`} type="info" style={{ marginBottom: 16 }} />
            <Form form={sendTemplateForm} layout="vertical" onFinish={handleSendTemplate}>
              <Form.Item name="recipients" label="Recipients (comma-separated emails)" rules={[{ required: true }]}>
                <Input placeholder="john@example.com, jane@example.com" />
              </Form.Item>
              <Divider>Fill in Variables</Divider>
              {selectedTemplate.variables?.split(',').filter(Boolean).map((v: string, i: number) => (
                <Form.Item key={i} name={['variable_pairs', i, 'key']} hidden initialValue={v.trim()}><Input /></Form.Item>
              ))}
              {selectedTemplate.variables?.split(',').filter(Boolean).map((v: string, i: number) => (
                <Form.Item key={`val_${i}`} name={['variable_pairs', i, 'value']} label={v.trim()}>
                  <Input placeholder={`Value for ${v.trim()}`} />
                </Form.Item>
              ))}
              <Space><Button type="primary" htmlType="submit" icon={<SendOutlined />}>Send Email</Button><Button onClick={() => setSendTemplateModal(false)}>Cancel</Button></Space>
            </Form>
          </div>
        )}
      </Modal>

      {/* ── Template Preview Modal ───────────────────────────────────────── */}
      <Modal title={`Preview — ${selectedTemplate?.template_name}`} open={previewModal}
        onCancel={() => setPreviewModal(false)} footer={[<Button key="close" onClick={() => setPreviewModal(false)}>Close</Button>]} width={650}>
        {selectedTemplate && (
          <div>
            <p><strong>Subject:</strong> {selectedTemplate.subject}</p>
            <p><strong>Type:</strong> <Tag color="blue">{selectedTemplate.template_type?.replace(/_/g,' ')}</Tag></p>
            <p><strong>Variables:</strong> {selectedTemplate.variables?.split(',').map((v: string, i: number) => <Tag key={i} color="cyan">{v.trim()}</Tag>)}</p>
            <Divider />
            <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 4, whiteSpace: 'pre-wrap', fontFamily: 'Arial', fontSize: 13 }}>
              {selectedTemplate.body}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Create Background Check Modal ────────────────────────────────── */}
      <Modal title="Initiate Background Check" open={bgModal}
        onCancel={() => { setBgModal(false); bgForm.resetFields(); }} footer={null} width={560}>
        <Alert message="Written candidate consent must be obtained before initiating any background check. Retain consent documentation." type="warning" showIcon style={{ marginBottom: 16 }} />
        <Form form={bgForm} layout="vertical" onFinish={handleCreateBg}>
          <Form.Item name="candidate_name" label="Candidate Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="check_type" label="Check Type" rules={[{ required: true }]}>
              <Select>{['criminal','credit','employment_history','education','reference','social_media','identity','all'].map(t => <Select.Option key={t} value={t}>{t.replace(/_/g,' ')}</Select.Option>)}</Select>
            </Form.Item></Col>
            <Col span={12}><Form.Item name="provider" label="Provider / Agency"><Input placeholder="e.g. FirstCheck, Sterling-Wms" /></Form.Item></Col>
          </Row>
          <Form.Item name="initiated_date" label="Initiation Date" initialValue={new Date().toISOString().split('T')[0]}><Input type="date" /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Initiate Check</Button><Button onClick={() => setBgModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* ── Email Background Check Report Modal ──────────────────────────── */}
      <Modal title="Email Background Check Report" open={bgReportModal}
        onCancel={() => { setBgReportModal(false); bgReportForm.resetFields(); }} footer={null} width={580}>
        <Alert message="This report is CONFIDENTIAL. Only send to authorised HR personnel, hiring managers, or legal counsel with a legitimate need to know." type="error" showIcon style={{ marginBottom: 16 }} />
        <Form form={bgReportForm} layout="vertical" onFinish={handleEmailReport}>
          <Form.Item name="recipients" label="Recipients (comma-separated emails)" rules={[{ required: true }]}>
            <TextArea rows={2} placeholder="hr@company.com, manager@company.com, legal@company.com" />
          </Form.Item>
          <Form.Item name="subject" label="Email Subject" initialValue={`Background Check Report — ${new Date().toLocaleDateString('en-NG')}`} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="message" label="Additional Message (optional)">
            <TextArea rows={3} placeholder="Please review the attached background check results for the above candidates..." />
          </Form.Item>
          <Alert message={`Report will include ${selectedBgChecks.length} background check(s).`} type="info" showIcon style={{ marginBottom: 16 }} />
          <Space>
            <Button type="primary" htmlType="submit" icon={<MailOutlined />} loading={sendingReport}>Send Report</Button>
            <Button onClick={() => setBgReportModal(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      {/* ── Job modals ───────────────────────────────────────────────────── */}
      <Modal title={editingJob ? 'Edit Job Posting' : 'Post New Job'} open={isFormModalVisible}
        onCancel={() => { setIsFormModalVisible(false); setEditingJob(null); }} footer={null} width={800}>
        <JobPostingForm job={editingJob} onSuccess={() => { setIsFormModalVisible(false); fetchData(); }}
          onCancel={() => { setIsFormModalVisible(false); setEditingJob(null); }} />
      </Modal>
      <Modal title="Job Posting Details" open={isDetailsModalVisible} onCancel={() => setIsDetailsModalVisible(false)} footer={null} width={800}>
        {selectedJob && <JobPostingDetails job={selectedJob} />}
      </Modal>
      <PDFExportModal visible={isPdfModalVisible} onCancel={() => setIsPdfModalVisible(false)} activeTab="jobs" />
    </div>
  );
};

export default RecruitmentHiring;
